(function () {
  // ---- CONFIG ----
  const DISCOUNT_MULTIPLIER = 0.85;         // 15% off
  const PRICE_SELECTOR = 'span.rc-price';   // target elements inside shadow DOM
  const WIDGET_TAG = 'recharge-subscription-widget'; // custom element to wait for
  const MAX_WAIT_TIME = 10000; // 10 seconds timeout

  let processedWidgets = new WeakSet(); // Track which widgets we've already processed

  /**
   * Get all price elements from inside Shadow DOM of Recharge widgets
   */
  function getPriceElementsFromShadowDOM() {
    const widgets = document.querySelectorAll(WIDGET_TAG);
    const allPriceElements = [];

    widgets.forEach(widget => {
      if (widget.shadowRoot) {
        const priceElements = widget.shadowRoot.querySelectorAll(PRICE_SELECTOR);
        allPriceElements.push(...priceElements);
      }
    });

    return allPriceElements;
  }

  /**
   * Add badge to subscription option
   */
  function addBadgeToSubscriptionOption() {
    const widgets = document.querySelectorAll(WIDGET_TAG);

    widgets.forEach(widget => {
      if (widget.shadowRoot) {
        const subscriptionDiv = widget.shadowRoot.querySelector('.rc-purchase-option.rc-purchase-option__subscription');

        if (subscriptionDiv && !subscriptionDiv.hasAttribute('data-badge-added')) {
          // Create the badge element
          const badge = document.createElement('span');
          badge.className = 'rc-purchase-option__badge';
          badge.setAttribute('aria-hidden', 'true');
          badge.setAttribute('part', 'rc-purchase-option__badge');
          badge.textContent = '15% off—applied at checkout';

          // Insert as the first child
          subscriptionDiv.insertBefore(badge, subscriptionDiv.firstChild);

          // Mark as processed
          subscriptionDiv.setAttribute('data-badge-added', 'true');
        }
      }
    });
  }

  /**
   * Update custom price display based on original price
   */
  function updateCustomPrice(element, priceWrapper) {
    const originalText = element.textContent.trim();
    const priceMatch = originalText.match(/[\d,]+\.?\d*/);

    if (priceMatch) {
      const originalPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
      const discountedPrice = originalPrice * DISCOUNT_MULTIPLIER;
      const currencySymbol = originalText.substring(0, originalText.indexOf(priceMatch[0]));

      // Update the wrapper content
      const originalPriceSpan = priceWrapper.querySelector('span:first-child');
      const discountedPriceSpan = priceWrapper.querySelector('span:last-child');

      if (originalPriceSpan && discountedPriceSpan) {
        originalPriceSpan.textContent = originalText;
        discountedPriceSpan.textContent = currencySymbol + discountedPrice.toFixed(2);
      }
    }
  }

  /**
   * Setup observer for a single price element to watch for changes
   */
  function watchPriceElement(element, priceWrapper) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData' || mutation.type === 'childList') {
          updateCustomPrice(element, priceWrapper);
        }
      });
    });

    // Observe the element for text changes
    observer.observe(element, {
      characterData: true,
      childList: true,
      subtree: true
    });
  }

  /**
   * Process and apply discount to price elements
   */
  function processPriceElements() {
    const priceElements = getPriceElementsFromShadowDOM();

    priceElements.forEach(element => {
      // Skip if already processed
      if (element.hasAttribute('data-discount-processed')) {
        return;
      }

      // Check if this price element is within a subscription option
      const subscriptionOption = element.closest('.rc-purchase-option__subscription');
      if (!subscriptionOption) {
        return;
      }

      element.setAttribute('data-discount-processed', 'true');

      // Extract price and apply discount
      const originalText = element.textContent.trim();
      const priceMatch = originalText.match(/[\d,]+\.?\d*/);

      if (priceMatch) {
        const originalPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
        const discountedPrice = originalPrice * DISCOUNT_MULTIPLIER;

        // Create strikethrough original price and discounted price
        const currencySymbol = originalText.substring(0, originalText.indexOf(priceMatch[0]));

        // Hide the original element more forcefully
        element.style.cssText = 'display: none !important; visibility: hidden !important; position: absolute !important; width: 0 !important; height: 0 !important; opacity: 0 !important;';

        // Create a new wrapper element to display our custom prices
        const priceWrapper = document.createElement('span');
        priceWrapper.className = 'rc-price-custom';
        priceWrapper.setAttribute('data-custom-price', 'true');
        priceWrapper.setAttribute('part', 'rc-purchase-option__discounted-price'); // Match the original part attribute

        // Create strikethrough original price
        const originalPriceSpan = document.createElement('span');
        originalPriceSpan.style.textDecoration = 'line-through';
        originalPriceSpan.style.opacity = '0.6';
        originalPriceSpan.style.marginRight = '8px';
        originalPriceSpan.textContent = originalText;

        // Create discounted price
        const discountedPriceSpan = document.createElement('span');
        discountedPriceSpan.style.fontWeight = 'bold';
        discountedPriceSpan.style.color = '#000000';
        discountedPriceSpan.textContent = currencySymbol + discountedPrice.toFixed(2);

        // Add both to wrapper
        priceWrapper.appendChild(originalPriceSpan);
        priceWrapper.appendChild(discountedPriceSpan);

        // Insert the wrapper after the original element
        element.parentNode.insertBefore(priceWrapper, element.nextSibling);

        // Watch for changes to the original price element
        watchPriceElement(element, priceWrapper);
      }
    });

    // Add badge after processing prices
    addBadgeToSubscriptionOption();
  }

  /**
   * Wait for Recharge widgets with shadow DOM to appear
   */
  function waitForWidgets() {
    // Check if widgets already exist
    const existingWidgets = document.querySelectorAll(WIDGET_TAG);

    if (existingWidgets.length > 0) {
      // Check if shadow roots are ready
      let hasContent = false;
      existingWidgets.forEach(widget => {
        if (widget.shadowRoot && widget.shadowRoot.querySelector(PRICE_SELECTOR)) {
          hasContent = true;
        }
      });

      if (hasContent) {
        processPriceElements();
        return;
      }
    }

    // Set up MutationObserver to watch for widgets being added or shadow DOM changes
    const observer = new MutationObserver((mutations) => {
      const widgets = document.querySelectorAll(WIDGET_TAG);

      if (widgets.length > 0) {
        // Check if any widget has shadow DOM with price elements
        let foundPrices = false;
        widgets.forEach(widget => {
          if (widget.shadowRoot) {
            const prices = widget.shadowRoot.querySelectorAll(PRICE_SELECTOR);
            if (prices.length > 0 && !processedWidgets.has(widget)) {
              foundPrices = true;
              processedWidgets.add(widget);
            }
          }
        });

        if (foundPrices) {
          processPriceElements();
          // Don't disconnect - there might be multiple widgets loading at different times
        }
      }
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Set timeout to stop observing after MAX_WAIT_TIME
    setTimeout(() => {
      observer.disconnect();
    }, MAX_WAIT_TIME);
  }

  /**
   * Initialize the script
   */
  function init() {
    // Wait for custom element to be defined
    if (customElements && typeof customElements.whenDefined === 'function') {
      customElements.whenDefined(WIDGET_TAG)
        .then(() => {
          // Small delay to let shadow DOM fully render
          setTimeout(waitForWidgets, 200);
        })
        .catch(() => {
          // Fallback: just start waiting
          waitForWidgets();
        });
    } else {
      // No custom elements API, start waiting immediately
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', waitForWidgets);
      } else {
        waitForWidgets();
      }
    }
  }

  // Start the script
  init();
})();
